import React, { useEffect } from 'react'
import styled from 'styled-components'

import breakpoints from '../../constants/breakpoints'
import theme from '../../theme'
import Check from '../../assets/circle-check.svg'
import Icon from '../../components/Icon'
import { Slide, Cards } from '../../types'
import { getNextSkipRoute } from '../../utils'

import { ModalConsumer } from '../../context/ModalContext'
import SlideContainer from '../../components/Layout/SlideContainer'
import Readspeaker from '../../components/Readspeaker'
import LinkButton from '../../components/LinkButton'

interface Props {
  align?: string
  title: string
  description?: string
  cards: Cards[]
  button: string
  nextLink: string
  routeIndex?: string
  routes: Slide[]
  routeId: string
  onSkipRoute: (path: string) => void
}

const PopupCards: React.FC<Props> = ({
  align,
  title,
  description,
  cards,
  button,
  nextLink,
  routeIndex,
  routes,
  routeId,
  onSkipRoute
}) => {
  const cardsInStorage = localStorage.getItem('cardsRead')
  const cardsArray = cardsInStorage !== null ? JSON.parse(cardsInStorage) : [];

  useEffect(() => {
    const nextRoute = routeIndex && getNextSkipRoute(routeIndex, routes)
    nextRoute && onSkipRoute(nextRoute.path)
  }, [])

  const handleLocalStorage = (index: number, currentRoute: string) => {
    if (cardsArray && !cardsArray.includes(currentRoute + '-' + index)) {
      cardsArray.push(currentRoute + '-' + index)
    }

    localStorage.setItem('cardsRead', JSON.stringify(cardsArray));
  }

  return (
    <SlideContainer>
      <Content className="text-formatted" $align={align}>
        <h1>{title}</h1>
        <Readspeaker/>

        {description && (
            <Description dangerouslySetInnerHTML={{ __html: description }}></Description>
          )}

        {cards.map((card, index) => {
          return (
            <ModalConsumer key={index}>
              {({ openModal }) => (
                <Card key={index}
                  href="#"
                  $cardWasViewed={cardsArray.includes(routeId + '-' + index)}
                  onClick={e => {
                    e.preventDefault()
                    openModal(card.cardTitle, card.modalContent, card.videos)
                    handleLocalStorage(index, routeId)
                  }}>
                  {
                    card.image && (
                      <CardImage>
                        <img src={card.image.src} alt={card.image.alt}></img>
                      </CardImage>
                    )
                  }
                  <CardText>
                    <h2>{card.cardTitle}</h2>
                    <p>{card.readingTime}</p>

                    {cardsArray && cardsArray.includes(routeId + '-' + index) && (
                      <CardWasViewed>
                        <Icon icon={Check} />
                        Bekeken</CardWasViewed>
                    )}
                  </CardText>
                </Card>
              )}
            </ModalConsumer>
            )
          })}

        {nextLink && (
          <Next>
            <LinkButton to={nextLink}>{button}</LinkButton>
          </Next>
        )}
      </Content>
    </SlideContainer>
  )
}

const Content = styled.div<{ $align: string | undefined }>`
  text-align: ${({ $align }) => ($align ? $align : 'center')};
  max-width: 100%;

  @media (min-width: ${breakpoints.sm}) {
    max-width: 700px;
  }

  & h1 {
    text-align: center;
  }
`

const Description = styled.div`
  margin: 0 0 10px;
  font-size: 1.125rem;
  margin-bottom: 60px;
`

const Next = styled.div`
  margin-top: 35px;
  width: 100%;

  @media (min-width: ${breakpoints.md}) {
    margin: 60px auto 35px;
    max-width: 700px;
  }
`

const Card = styled.a<{$cardWasViewed: string}>`
  display: flex;
  position: relative;
  border: solid 1px ${theme.lightGrey};
  margin-bottom: 24px;
  text-decoration: none;

  ${({ $cardWasViewed }) =>
  $cardWasViewed && `
    background-color: ${theme.lighterGrey};
    border-color: ${theme.lighterGrey};
  `
}
`

const CardImage = styled.div`
  display: none;

  @media (min-width: ${breakpoints.md}) {
    width: 140px;
    display: block;
  }

  & img {
    display: block;
    height: 100%;
    width: 100%;
    object-fit: cover;
  }
`

const CardText = styled.div`
  flex: 1;
  padding: 40px 26px;

  & h2 {
    margin: 0 0 15px;
    font-size: 18px;
    color: ${theme.textColor};
  }

  & p {
    margin: 0;
    font-size: 14px;
    color: ${theme.warmGrey};
  }
`

const CardWasViewed = styled.div`
  position: absolute;
  right: 18px;
  bottom: 18px;
  font-size: 16px;
  color: ${theme.green};
  display: flex;

  @media (min-width: ${breakpoints.md}) {
    top: 18px;
  }

  & svg {
    width: 16px;
    height: 16px;
    fill: ${theme.green};
    position: absolute;
    left: -25px;
    top: 4px;
  }
`

export default PopupCards
